import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { Globe, Server, Palette, Mail, ShieldCheck, Loader2, CheckCircle, Info, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Grille de prix (2026-09-10) ──────────────────────────────────────────────
// Prix des postes techniques tiers (domaine, hebergement, email) verifies sur
// les tarifs reels du marche (OVH, Hostinger, Google Workspace) pour que la
// transparence annoncee au client repose sur de vrais chiffres, pas des
// estimations en l'air. Les postes de conception/developpement reprennent
// (eclates a la carte) les memes montants que les 3 anciens forfaits fixes
// Starter/Business/E-Commerce.
const DOMAIN_OPTIONS = [
  { id: 'domain_fr', label: 'Nom de domaine .fr', price: 9, period: '/an', desc: "Reservation aupres d'un registraire officiel, renouvelable chaque annee." },
  { id: 'domain_com', label: 'Nom de domaine .com', price: 13, period: '/an', desc: "Extension internationale, renouvelable chaque annee." },
];

const HOSTING_OPTIONS = [
  { id: 'hosting_mutualise', label: 'Hebergement mutualise', price: 5, period: '/mois', desc: "Suffisant pour un site vitrine sans gros trafic." },
  { id: 'hosting_vps', label: 'Hebergement VPS', price: 10, period: '/mois', desc: "Plus de puissance et de fiabilite, recommande pour une boutique en ligne ou un site a fort trafic." },
];

const DESIGN_OPTIONS = [
  { id: 'design_template', label: 'Design template pre-concu', price: 0, period: '', desc: "Mise en page professionnelle existante, personnalisee a vos couleurs et votre logo." },
  { id: 'design_surmesure', label: 'Design 100% sur-mesure', price: 600, period: '', desc: "Maquette entierement pensee pour votre identite visuelle, aucun autre site ne lui ressemblera." },
];

const RECURRING_ITEMS = [
  { id: 'email_pro', icon: Mail, label: 'Adresse email professionnelle', price: 7, period: '/mois', desc: "Boite mail a votre nom de domaine (ex: contact@votresite.fr), via Google Workspace." },
  { id: 'backups', icon: ShieldCheck, label: 'Sauvegardes automatiques', price: 3, period: '/mois', desc: "Copie de securite reguliere de votre site, pour tout restaurer en cas de probleme." },
];

const ONE_TIME_ITEMS = [
  { id: 'blog', label: 'Blog integre', price: 200, desc: "Une section actualites/articles que vous pouvez alimenter vous-meme." },
  { id: 'cms_admin', label: 'Espace admin (modifiable par vous-meme)', price: 300, desc: "Un tableau de bord simple pour changer textes et images sans nous solliciter." },
  { id: 'ecommerce', label: 'Boutique en ligne complete', price: 1500, desc: "Catalogue produits, panier, gestion des commandes." },
  { id: 'paiement_ligne', label: 'Paiement en ligne (Stripe/PayPal)', price: 200, desc: "Vos clients paient directement sur le site, en carte bancaire." },
  { id: 'seo', label: 'Referencement naturel (SEO) de base', price: 300, desc: "Optimisation pour etre mieux trouve sur Google." },
  { id: 'redaction', label: 'Redaction de contenu par nos soins', price: 250, desc: "Nous ecrivons les textes de votre site a partir de vos informations, vous n'avez rien a taper." },
];

const BASE_SITE_PRICE = 600; // Site vitrine jusqu'a 5 pages, toujours inclus (fondation du site)
const EXTRA_PAGE_PRICE = 80;

export const DevisSimulateur = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [contact, setContact] = useState({ name: '', email: '', phone: '', country_code: '+33', needs: '' });

  const [domain, setDomain] = useState('domain_fr');
  const [hosting, setHosting] = useState('hosting_mutualise');
  const [design, setDesign] = useState('design_template');
  const [recurringChecked, setRecurringChecked] = useState({});
  const [oneTimeChecked, setOneTimeChecked] = useState({});
  const [extraPages, setExtraPages] = useState(0);

  const toggleRecurring = (id) => setRecurringChecked((s) => ({ ...s, [id]: !s[id] }));
  const toggleOneTime = (id) => setOneTimeChecked((s) => ({ ...s, [id]: !s[id] }));

  // ── Calcul des totaux, separe une fois / recurrent pour rester honnete sur
  // ce qui se paie une seule fois et ce qui revient chaque mois. ──────────────
  const breakdown = useMemo(() => {
    const oneTime = [];
    const recurringMonthly = [];
    const recurringYearly = [];

    const domainOpt = DOMAIN_OPTIONS.find((o) => o.id === domain);
    if (domainOpt) recurringYearly.push({ description: `${domainOpt.label} (par an)`, unit_price: domainOpt.price });

    const hostingOpt = HOSTING_OPTIONS.find((o) => o.id === hosting);
    if (hostingOpt) recurringMonthly.push({ description: `${hostingOpt.label} (par mois)`, unit_price: hostingOpt.price });

    const designOpt = DESIGN_OPTIONS.find((o) => o.id === design);
    if (designOpt && designOpt.price > 0) oneTime.push({ description: designOpt.label, unit_price: designOpt.price });

    oneTime.push({ description: 'Site vitrine jusqu\'a 5 pages', unit_price: BASE_SITE_PRICE });
    if (extraPages > 0) oneTime.push({ description: 'Page supplementaire', quantity: extraPages, unit_price: EXTRA_PAGE_PRICE });

    RECURRING_ITEMS.forEach((item) => {
      if (recurringChecked[item.id]) recurringMonthly.push({ description: `${item.label} (par mois)`, unit_price: item.price });
    });
    ONE_TIME_ITEMS.forEach((item) => {
      if (oneTimeChecked[item.id]) oneTime.push({ description: item.label, unit_price: item.price });
    });

    const oneTimeTotal = oneTime.reduce((s, i) => s + i.unit_price * (i.quantity || 1), 0);
    const monthlyTotal = recurringMonthly.reduce((s, i) => s + i.unit_price, 0);
    const yearlyTotal = recurringYearly.reduce((s, i) => s + i.unit_price, 0);

    return { oneTime, recurringMonthly, recurringYearly, oneTimeTotal, monthlyTotal, yearlyTotal };
  }, [domain, hosting, design, extraPages, recurringChecked, oneTimeChecked]);

  const handleContactChange = (e) => setContact((c) => ({ ...c, [e.target.name]: e.target.value }));

  const canGoStep2 = contact.name.trim() && contact.email.trim() && contact.phone.trim() && contact.needs.trim().length > 10;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const clientRes = await axios.post(`${API}/clients`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        country_code: contact.country_code,
        branch: 'siteweb',
        needs: contact.needs,
      });

      const items = [
        ...breakdown.oneTime.map((i) => ({ description: i.description, quantity: i.quantity || 1, unit_price: i.unit_price })),
        ...breakdown.recurringMonthly.map((i) => ({ description: i.description, quantity: 1, unit_price: i.unit_price })),
        ...breakdown.recurringYearly.map((i) => ({ description: i.description, quantity: 1, unit_price: i.unit_price })),
      ];

      // Devis en brouillon : le total simule sert de base transparente, mais
      // reste a valider et completer (frais de maintenance) avant envoi reel
      // au client - voir Admin.js, onglet Devis.
      const quoteRes = await axios.post(`${API}/quotes`, {
        client_id: clientRes.data.id,
        items,
        notes: `Devis simule par le client via le simulateur en ligne. Activite decrite : ${contact.needs}`,
      });

      // Emails (notification admin + accuse de reception client). Non bloquant :
      // si l'envoi echoue, le parcours client aboutit quand meme.
      axios.post(`${API}/notify/devis-simulation`, {
        client_name: contact.name,
        client_email: contact.email,
        phone: contact.phone,
        needs: contact.needs,
        total: breakdown.oneTimeTotal,
        quote_id: quoteRes.data.id,
      }).catch(() => {});

      setStep(3);
    } catch (error) {
      console.error('Error submitting simulation:', error);
      toast.error("Une erreur est survenue, merci de reessayer.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="devis-simulateur-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-10">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
            Etablissez votre devis par simulation
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Cochez ce dont vous avez besoin, le prix se met a jour en direct. Tarifs reels et transparents,
            sans surprise.
          </p>
        </div>

        {step === 1 && (
          <Card className="shadow-xl border-0" data-testid="simulateur-step1">
            <CardHeader className="border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                Vos coordonnees
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet</Label>
                  <Input id="name" name="name" value={contact.name} onChange={handleContactChange} data-testid="sim-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" value={contact.email} onChange={handleContactChange} data-testid="sim-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telephone</Label>
                <Input id="phone" name="phone" value={contact.phone} onChange={handleContactChange} placeholder="06 12 34 56 78" data-testid="sim-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="needs">Decrivez votre activite</Label>
                <Textarea
                  id="needs"
                  name="needs"
                  rows={4}
                  value={contact.needs}
                  onChange={handleContactChange}
                  placeholder="Ex : Je suis coach sportif independant, je veux un site pour presenter mes services et prendre des rendez-vous en ligne."
                  data-testid="sim-needs"
                />
              </div>
              <Button
                onClick={() => setStep(2)}
                disabled={!canGoStep2}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6"
                data-testid="sim-next-btn"
              >
                Continuer vers la simulation
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="simulateur-step2">
            {/* Nom de domaine */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> Nom de domaine</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={domain} onValueChange={setDomain} className="space-y-3">
                  {DOMAIN_OPTIONS.map((opt) => (
                    <OptionRow key={opt.id} type="radio" id={opt.id} checked={domain === opt.id} label={opt.label} price={opt.price} period={opt.period} desc={opt.desc} />
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Hebergement */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Server className="w-5 h-5 text-primary" /> Hebergement</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={hosting} onValueChange={setHosting} className="space-y-3">
                  {HOSTING_OPTIONS.map((opt) => (
                    <OptionRow key={opt.id} type="radio" id={opt.id} checked={hosting === opt.id} label={opt.label} price={opt.price} period={opt.period} desc={opt.desc} />
                  ))}
                </RadioGroup>
                <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Certificat SSL (https) inclus gratuitement, quel que soit votre choix.</p>
              </CardContent>
            </Card>

            {/* Design */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Palette className="w-5 h-5 text-primary" /> Design</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={design} onValueChange={setDesign} className="space-y-3">
                  {DESIGN_OPTIONS.map((opt) => (
                    <OptionRow key={opt.id} type="radio" id={opt.id} checked={design === opt.id} label={opt.label} price={opt.price} period={opt.period} desc={opt.desc} />
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Pages */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Nombre de pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="font-medium text-sm">Site vitrine jusqu'a 5 pages</p>
                    <p className="text-xs text-slate-500">Toujours inclus, c'est la base de votre site.</p>
                  </div>
                  <span className="font-semibold text-primary">{BASE_SITE_PRICE}€</span>
                </div>
                <div className="flex items-center justify-between p-3 mt-2">
                  <div>
                    <p className="font-medium text-sm">Pages supplementaires</p>
                    <p className="text-xs text-slate-500">{EXTRA_PAGE_PRICE}€ par page au-dela des 5 premieres.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setExtraPages((n) => Math.max(0, n - 1))} className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-slate-100" data-testid="sim-pages-minus">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-6 text-center font-medium">{extraPages}</span>
                    <button type="button" onClick={() => setExtraPages((n) => n + 1)} className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-slate-100" data-testid="sim-pages-plus">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fonctionnalites (une fois) */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Fonctionnalites (cout unique)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {ONE_TIME_ITEMS.map((item) => (
                  <OptionRow key={item.id} type="checkbox" id={item.id} checked={!!oneTimeChecked[item.id]} onToggle={() => toggleOneTime(item.id)} label={item.label} price={item.price} period="" desc={item.desc} />
                ))}
              </CardContent>
            </Card>

            {/* Services recurrents */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Services recurrents (optionnels)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {RECURRING_ITEMS.map((item) => (
                  <OptionRow key={item.id} type="checkbox" id={item.id} checked={!!recurringChecked[item.id]} onToggle={() => toggleRecurring(item.id)} label={item.label} price={item.price} period={item.period} desc={item.desc} />
                ))}
              </CardContent>
            </Card>

            {/* Recap sticky */}
            <Card className="shadow-xl border-2 border-primary sticky bottom-4" data-testid="sim-recap">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Cout de depart</p>
                    <p className="text-2xl font-bold text-slate-900">{breakdown.oneTimeTotal.toFixed(0)}€</p>
                  </div>
                  <Separator orientation="vertical" className="h-10 hidden sm:block" />
                  <div>
                    <p className="text-sm text-slate-500">Puis par mois</p>
                    <p className="text-2xl font-bold text-primary">{breakdown.monthlyTotal.toFixed(0)}€/mois</p>
                  </div>
                  <Separator orientation="vertical" className="h-10 hidden sm:block" />
                  <div>
                    <p className="text-sm text-slate-500">Plus, par an</p>
                    <p className="text-2xl font-bold text-slate-900">{breakdown.yearlyTotal.toFixed(0)}€/an</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3 flex items-start gap-1">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Ce montant couvre les couts techniques reels et la conception de base. Les frais de suivi et de
                  maintenance personnalisee seront ajoutes a votre devis final, que nous vous enverrons apres etude
                  de votre activite.
                </p>
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-full" data-testid="sim-back-btn">Retour</Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full gap-2"
                    data-testid="sim-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Recevoir mon devis personnalise'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 3 && (
          <Card className="shadow-xl border-0 text-center" data-testid="simulateur-step3">
            <CardContent className="p-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-slate-900 mb-3">Simulation envoyee !</h2>
              <p className="text-slate-600 mb-8 max-w-md mx-auto">
                Nous etudions votre activite et vous recontactons sous 24h avec votre devis final. Vous pouvez des
                maintenant prendre rendez-vous pour en discuter de vive voix.
              </p>
              <Button onClick={() => navigate('/appointment')} className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6" data-testid="sim-appointment-btn">
                Prendre rendez-vous en visioconference
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Ligne d'option reutilisable (checkbox ou radio), toujours avec prix visible
// + explication courte : c'est le coeur de la "transparence" demandee.
const OptionRow = ({ type, id, checked, onToggle, label, price, period, desc }) => (
  <label
    htmlFor={id}
    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
      checked ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50'
    }`}
  >
    {type === 'radio' ? (
      <RadioGroupItem value={id} id={id} className="mt-1" />
    ) : (
      <Checkbox id={id} checked={checked} onCheckedChange={onToggle} className="mt-1" />
    )}
    <div className="flex-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm text-slate-900">{label}</span>
        <span className="font-semibold text-primary whitespace-nowrap">
          {price > 0 ? `${price}€${period}` : 'Inclus'}
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
    </div>
  </label>
);
